// Treatment / surgery catalogue. Each treatment links to a specialty + related doctors.
export const TREATMENTS = [
    // --- Cardiology ---
    {
        slug: "cabg",
        name: "Coronary Artery Bypass Grafting (CABG)",
        shortName: "CABG",
        specialty: "cardiology",
        tagline: "Restoring blood flow to the heart with proven surgical excellence.",
        overview:
            "CABG creates new pathways around blocked coronary arteries using grafts from the patient's own vessels, relieving angina and dramatically reducing the risk of heart attack.",
        procedure:
            "Performed under general anaesthesia, typically beating-heart (off-pump) or with cardiopulmonary bypass. The surgeon harvests the internal mammary artery and/or radial/saphenous vessels and grafts them onto the coronaries beyond the blockages.",
        recovery:
            "ICU stay 1–2 days; total hospital stay 6–9 days; full recovery 6–8 weeks with cardiac rehab.",
        cost: "Starting from $5,500",
        duration: "4–6 hours",
        hospitalStay: "7–10 days",
        successRate: "98%",
        image: "https://res.cloudinary.com/deq3rnrha/image/upload/v1778866404/CABG_hjxvuf.jpg",
        doctors: ["naresh-trehan"],
    },
    {
        slug: "angioplasty",
        name: "Coronary Angioplasty & Stenting",
        shortName: "Angioplasty",
        specialty: "cardiology",
        tagline: "Minimally invasive treatment for blocked heart arteries.",
        overview:
            "A balloon-tipped catheter opens narrowed coronary arteries and a drug-eluting stent is deployed to keep them patent — performed under local anaesthesia via the wrist or groin.",
        procedure:
            "The interventional cardiologist accesses the radial artery, threads a guidewire across the blockage, dilates with a balloon and deploys a drug-eluting stent under fluoroscopic guidance.",
        recovery: "Discharge in 24–48 hours; return to work in 1 week.",
        cost: "Starting from $3,500",
        duration: "45–90 minutes",
        hospitalStay: "1–2 days",
        successRate: "98%",
        image: "https://res.cloudinary.com/deq3rnrha/image/upload/v1778866404/angioplasty_gzb6yc.jpg",
        doctors: ["naresh-trehan"],
    },
    {
        slug: "valve-replacement",
        name: "Heart Valve Replacement",
        shortName: "Valve Replacement",
        specialty: "cardiology",
        tagline: "Surgical or transcatheter valve replacement for failing heart valves.",
        overview:
            "Diseased aortic or mitral valves are replaced with mechanical or tissue prostheses, restoring normal cardiac function.",
        procedure:
            "Performed via median sternotomy or minimally invasive thoracotomy, on cardiopulmonary bypass. Mechanical or biological valves are selected based on patient age and anticoagulation considerations.",
        recovery: "ICU 1–2 days; hospital 7–10 days; full recovery 8–12 weeks.",
        cost: "Starting from $6,800",
        duration: "4–6 hours",
        hospitalStay: "8–10 days",
        successRate: "97%",
        image: "https://res.cloudinary.com/deq3rnrha/image/upload/v1778866404/valve_replacement_utf82e.jpg",
        doctors: ["naresh-trehan"],
    },
    {
        slug: "tavr",
        name: "TAVR / TAVI — Transcatheter Aortic Valve Replacement",
        shortName: "TAVR",
        specialty: "cardiology",
        tagline: "Replacing the aortic valve without open-heart surgery.",
        overview:
            "TAVR delivers a new aortic valve through a catheter inserted in the femoral artery — ideal for high-risk or elderly patients who cannot tolerate conventional surgery.",
        procedure:
            "Performed in a hybrid cath-lab under sedation or general anaesthesia. The collapsible valve is delivered through a sheath in the femoral artery and expanded inside the diseased aortic valve.",
        recovery: "Hospital 3–5 days; back to normal activity in 2 weeks.",
        cost: "Starting from $22,000",
        duration: "1–2 hours",
        hospitalStay: "3–5 days",
        successRate: "95%",
        image: "https://res.cloudinary.com/deq3rnrha/image/upload/v1778866404/TAVR_mijafu.jpg",
        doctors: ["naresh-trehan"],
    },
    {
        slug: "pacemaker",
        name: "Heart Pacemaker Implantation",
        shortName: "Heart Pacemaker",
        specialty: "cardiology",
        tagline: "Permanent pacemakers and ICDs for arrhythmias and bradycardia.",
        overview:
            "A small device implanted under the skin sends electrical signals to keep the heart beating at a normal rate — life-changing therapy for patients with slow or irregular heart rhythms.",
        procedure:
            "Performed under local anaesthesia in a cath-lab. Leads are advanced through a vein into the heart chambers and connected to a pulse generator placed below the collarbone. Single, dual or biventricular (CRT) devices are selected based on indication.",
        recovery:
            "Discharge in 24–48 hours; light activity from day 2; arm-overhead restriction for 4 weeks; back to normal life in 6 weeks.",
        cost: "Starting from $4,800",
        duration: "1–2 hours",
        hospitalStay: "1–2 days",
        successRate: "99%",
        image: "https://res.cloudinary.com/deq3rnrha/image/upload/v1776764634/Cardiac_Surgery_ms5pft.jpg",
        doctors: ["naresh-trehan"],
    },

    // --- Orthopaedics ---
    {
        slug: "knee-replacement",
        name: "Total Knee Replacement",
        shortName: "Knee Replacement",
        specialty: "orthopedics",
        tagline: "Pain-free walking with India's most experienced knee surgeons.",
        overview:
            "The arthritic knee joint is replaced with a precisely sized prosthesis, restoring alignment and eliminating pain.",
        procedure:
            "Performed with computer navigation or robotic assistance for sub-millimetre accuracy, under spinal or general anaesthesia.",
        recovery:
            "Walking with support on day 1; discharge in 4–5 days; full recovery 6–12 weeks.",
        cost: "Starting from $5,200",
        duration: "1.5–2 hours per knee",
        hospitalStay: "4–6 days",
        successRate: "98%",
        image: "https://res.cloudinary.com/deq3rnrha/image/upload/v1778868880/ChatGPT_Image_May_15_2026_11_38_58_PM_wtbbli.png",
        doctors: ["hemant-sharma"],
    },
    {
        slug: "hip-replacement",
        name: "Total Hip Replacement",
        shortName: "Hip Replacement",
        specialty: "orthopedics",
        tagline: "Restoring mobility for patients with advanced hip arthritis or AVN.",
        overview:
            "Diseased hip joint surfaces are replaced with ceramic or metal-poly implants, dramatically reducing pain and restoring function.",
        procedure: "Anterior or posterior approach, often with robotic navigation.",
        recovery: "Walk with support on day 1; discharge 4–5 days; full recovery 6–12 weeks.",
        cost: "Starting from $6,400",
        duration: "1.5–2 hours",
        hospitalStay: "4–6 days",
        successRate: "97%",
        image: "https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=1600&q=80",
        doctors: ["hemant-sharma"],
    },
    {
        slug: "spine-surgery",
        name: "Spine Surgery (Minimally Invasive)",
        shortName: "Spine Surgery",
        specialty: "orthopedics",
        tagline: "Endoscopic and robotic spine procedures — same-day mobilisation.",
        overview:
            "From microdiscectomy to instrumented fusion, our partners offer the full spectrum of spinal surgery with the latest minimally invasive techniques.",
        procedure:
            "Tubular retractors, intraoperative neuromonitoring and navigation reduce muscle damage and improve precision.",
        recovery: "Walk same day; discharge 2–5 days; return to office in 3–4 weeks.",
        cost: "Starting from $4,800",
        duration: "2–4 hours",
        hospitalStay: "3–5 days",
        successRate: "96%",
        image: "https://images.unsplash.com/photo-1551076805-e1869033e561?w=1600&q=80",
        doctors: ["hemant-sharma", "sandeep-vaishya"],
    },
    {
        slug: "acl-reconstruction",
        name: "ACL Reconstruction",
        shortName: "ACL Reconstruction",
        specialty: "orthopedics",
        tagline: "Arthroscopic ligament reconstruction for sports injuries.",
        overview:
            "The torn anterior cruciate ligament is reconstructed using a hamstring or patellar tendon graft, restoring knee stability.",
        procedure: "Arthroscopic, day-care or 1 night stay.",
        recovery: "Brace 4 weeks; return to sports 6–9 months.",
        cost: "Starting from $3,200",
        duration: "1–1.5 hours",
        hospitalStay: "1–2 days",
        successRate: "95%",
        image: "https://res.cloudinary.com/deq3rnrha/image/upload/v1778868880/ChatGPT_Image_May_15_2026_11_41_52_PM_ajkw3a.png",
        doctors: ["hemant-sharma"],
    },
    {
        slug: "spine-surgery-tlif",
        name: "Spine Surgery — TLIF (Transforaminal Lumbar Interbody Fusion)",
        shortName: "Spine Surgery (TLIF)",
        specialty: "orthopedics",
        tagline: "Minimally invasive lumbar fusion for nerve compression and instability.",
        overview:
            "TLIF combines decompression and fusion through a single posterior approach, restoring disc height, stabilising the spine and relieving radicular leg pain caused by disc disease, spondylolisthesis or stenosis.",
        procedure:
            "Performed under general anaesthesia with intraoperative neuromonitoring and image-guided pedicle screw placement. The disc is removed, a cage filled with bone graft is inserted via the foramen and pedicle screws + rods stabilise the segment.",
        recovery:
            "Walk same day or day 1; discharge in 3–5 days; brace for 6 weeks; return to office in 6–8 weeks; full fusion at 6 months.",
        cost: "Starting from $7,800",
        duration: "3–5 hours",
        hospitalStay: "4–6 days",
        successRate: "95%",
        image: "https://res.cloudinary.com/deq3rnrha/image/upload/v1778956082/ChatGPT_Image_May_16_2026_11_57_48_PM_gcuvsi.png",
        doctors: ["hemant-sharma"],
    },

    // --- Oncology ---
    {
        slug: "chemotherapy",
        name: "Chemotherapy",
        shortName: "Chemotherapy",
        specialty: "oncology",
        tagline: "Protocol-driven medical oncology with personalised plans.",
        overview:
            "We coordinate evaluation, port placement, infusion cycles and supportive care in line with NCCN/ESMO guidelines.",
        procedure: "Outpatient infusion centres with rapid turnaround and dedicated oncology nursing.",
        recovery: "Variable by protocol; coordinator schedules monitoring and side-effect management.",
        cost: "Per cycle — quote on enquiry",
        duration: "Cycle-based",
        hospitalStay: "Outpatient or short-stay",
        successRate: "Tumour-dependent",
        image: "https://res.cloudinary.com/deq3rnrha/image/upload/v1779006268/Chemo_fnf9ei.jpg",
        doctors: ["rahul-bhargava"],
    },
    {
        slug: "radiation-therapy",
        name: "Radiation Therapy (IMRT, IGRT, CyberKnife)",
        shortName: "Radiation Therapy",
        specialty: "oncology",
        tagline: "High-precision radiotherapy that spares healthy tissue.",
        overview:
            "Latest linear accelerators with image-guided and intensity-modulated capabilities deliver tumour-conforming doses with sub-millimetre accuracy.",
        procedure: "Simulation, planning, daily treatment over 4–6 weeks (varies).",
        recovery: "Side-effects managed by radiation oncologist; rapid normalisation post-treatment.",
        cost: "Starting from $4,500",
        duration: "4–6 weeks",
        hospitalStay: "Outpatient",
        successRate: "Tumour-dependent",
        image: "https://images.unsplash.com/photo-1551076805-e1869033e561?w=1600&q=80",
        doctors: ["rahul-bhargava"],
    },
    {
        slug: "robotic-cancer-surgery",
        name: "Robotic Cancer Surgery",
        shortName: "Robotic Cancer Surgery",
        specialty: "oncology",
        tagline: "Precision oncologic surgery with the da Vinci platform.",
        overview:
            "Robotic-assisted surgery for prostate, kidney, head & neck, gynae and colorectal cancers — smaller incisions, less blood loss, faster recovery.",
        procedure: "Performed under general anaesthesia with 3D high-definition visualisation.",
        recovery: "Discharge 3–5 days; back to office in 3 weeks.",
        cost: "Starting from $9,800",
        duration: "3–5 hours",
        hospitalStay: "3–5 days",
        successRate: "97%",
        image: "https://res.cloudinary.com/deq3rnrha/image/upload/v1779006268/robotic_surgery_cz1nxf.jpg",
        doctors: ["rahul-bhargava"],
    },
    {
        slug: "bmt",
        name: "Bone Marrow Transplant (BMT)",
        shortName: "Bone Marrow Transplant",
        specialty: "oncology",
        tagline: "Autologous and allogeneic BMT for blood cancers and disorders.",
        overview:
            "Comprehensive BMT programme covering thalassaemia, leukaemia, lymphoma, multiple myeloma and aplastic anaemia.",
        procedure: "Conditioning chemotherapy followed by stem cell infusion under HEPA-filtered BMT unit care.",
        recovery: "4–6 weeks in-hospital; 3–6 months outpatient follow-up.",
        cost: "Starting from $24,000",
        duration: "Inpatient programme",
        hospitalStay: "4–6 weeks",
        successRate: "Disease-dependent",
        image: "https://res.cloudinary.com/deq3rnrha/image/upload/v1779006267/BMT1_n94zeo.jpg",
        doctors: ["rahul-bhargava"],
    },

    // --- GI / Liver ---
    {
        slug: "liver-transplant",
        name: "Liver Transplantation",
        shortName: "Liver Transplant",
        specialty: "gastroenterology",
        tagline: "Living-donor and deceased-donor liver transplant programmes.",
        overview:
            "India is one of the world's leading destinations for LDLT, with combined experience of over 25,000 transplants and 1-year survival exceeding 90%.",
        procedure: "Donor hepatectomy and recipient transplantation performed by dedicated HPB teams in a single operating session.",
        recovery: "ICU 3–5 days; hospital 3–4 weeks; long-term immunosuppression.",
        cost: "Starting from $34,000",
        duration: "8–12 hours",
        hospitalStay: "3–4 weeks",
        successRate: "92%",
        image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1600&q=80",
        doctors: ["vivek-vij"],
    },
    {
        slug: "bariatric-surgery",
        name: "Bariatric Surgery",
        shortName: "Bariatric Surgery",
        specialty: "bariatric",
        tagline: "Sleeve gastrectomy and gastric bypass for sustained weight loss.",
        overview:
            "Laparoscopic sleeve gastrectomy and Roux-en-Y gastric bypass with structured nutrition, endocrinology and follow-up.",
        procedure: "Laparoscopic, 1.5–3 hours under general anaesthesia.",
        recovery: "Hospital 3–4 days; back to office in 2 weeks; durable weight loss over 12–18 months.",
        cost: "Starting from $7,500",
        duration: "1.5–3 hours",
        hospitalStay: "3–4 days",
        successRate: "96%",
        image: "https://images.unsplash.com/photo-1559757175-7cb036bd4d31?w=1600&q=80",
        doctors: [],
    },

    // --- Neuro ---
    {
        slug: "brain-tumour",
        name: "Brain Tumour Surgery",
        shortName: "Brain Tumour Surgery",
        specialty: "neurology",
        tagline: "Awake craniotomy and image-guided tumour resection.",
        overview:
            "Complex brain tumours are removed with intra-operative MRI, neuromonitoring and awake mapping when needed, maximising tumour resection while preserving function.",
        procedure: "Performed in specialised neuro-OTs by dedicated teams.",
        recovery: "ICU 1–2 days; hospital 7–10 days; rehab as required.",
        cost: "Starting from $9,800",
        duration: "4–8 hours",
        hospitalStay: "7–10 days",
        successRate: "Tumour-dependent",
        image: "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=1600&q=80",
        doctors: ["sandeep-vaishya"],
    },
    {
        slug: "dbs",
        name: "Deep Brain Stimulation (DBS)",
        shortName: "DBS",
        specialty: "neurology",
        tagline: "Functional neurosurgery for Parkinson's and movement disorders.",
        overview:
            "Implantation of stimulation electrodes into targeted brain nuclei, dramatically improving quality of life in Parkinson's and dystonia.",
        procedure: "Stereotactic implantation under awake conditions with intraoperative testing.",
        recovery: "Hospital 5–7 days; programming visits over 6–12 months.",
        cost: "Starting from $24,500",
        duration: "5–7 hours",
        hospitalStay: "5–7 days",
        successRate: "92%",
        image: "https://res.cloudinary.com/deq3rnrha/image/upload/v1778867697/DBS_ljpbdv.jpg",
        doctors: ["sandeep-vaishya"],
    },
    {
        slug: "spine-tumour",
        name: "Spine Tumour Surgery",
        shortName: "Spine Tumour Surgery",
        specialty: "neurology",
        tagline: "Precision surgery for intramedullary, extramedullary and vertebral spine tumours.",
        overview:
            "Spine tumours — whether benign, malignant, primary or metastatic — are removed using intra-operative neuromonitoring, navigation and (where needed) instrumented stabilisation, preserving neurological function and spinal stability.",
        procedure:
            "Performed under general anaesthesia with neuromonitoring of motor and sensory pathways. Microsurgical or endoscopic resection is combined with instrumented fusion when stability is compromised. Adjuvant radiotherapy is planned in a tumour board setting where required.",
        recovery:
            "ICU 1–2 days; hospital 7–10 days; rehabilitation as required for motor recovery. Most patients walk within 2–5 days post-op.",
        cost: "Starting from $8,500",
        duration: "4–8 hours",
        hospitalStay: "7–10 days",
        successRate: "Tumour-dependent",
        image: "https://res.cloudinary.com/deq3rnrha/image/upload/v1778867641/SPine_tumor_wupgsh.jpg",
        doctors: ["sandeep-vaishya"],
    },
];

export const getTreatment = (slug) => TREATMENTS.find((t) => t.slug === slug);

export const getTreatmentsBySpecialty = (specSlug) =>
    TREATMENTS.filter((t) => t.specialty === specSlug);
